import React, { useMemo } from 'react'

import { IDashboardExample } from 'types/GlobalDashboardExample'
import { MetricStatus } from 'types/GlobalMetric'

import List from 'components_new/atoms/List'
import Text from 'components_new/atoms/Text'

import GenericListItem from 'components_new/molecules/GenericListItem'
import NavigationItem from 'components_new/molecules/NavigationItem'
import StatusBox from 'components_new/molecules/StatusBox'

import { useMetrics } from 'redux/hooks/Metrics'
import { arrayToObject } from 'utils/functions'
import { translateMetricStatus } from 'utils/enumTranslator'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'

interface MetricListProps {
  dashboard: IDashboardExample
}

const MetricList = (props: MetricListProps) => {
  const { dashboard } = props
  const { lang, translateEnum } = useTranslation()

  const [metrics] = useMetrics()
  const { isAdmin } = useAccountSettings()

  const [noAccess, unsupported] = useMemo(() => {
    const mapper = arrayToObject(metrics)

    const noAccess = dashboard.data.metrics
      .filter((metric) => !metric.access)
      .map((item) => mapper[item.id])
      .filter(Boolean)

    const unsupported = dashboard.data.metrics
      .filter((metric) => metric.status !== MetricStatus.ACTIVE)
      .map((item) => mapper[item.id])
      .filter(Boolean)

    return [
      noAccess.map((metric) => [
        <GenericListItem
          key={metric.id}
          size="medium"
          title={metric.title[lang]}
          titleIcon={{
            name: 'Private'
          }}
          variant="metric"
        />
      ]),
      unsupported.map((metric) => [
        isAdmin ? (
          <NavigationItem
            item={{
              chip: {
                color: translateMetricStatus[metric.status].color,
                label: translateEnum('metric_statuses', metric.status).title
              },
              href: `/metrics/${metric.id}`,
              icon: { name: 'Metric' },
              title: metric.title[lang]
            }}
            key={metric.id}
          />
        ) : (
          <GenericListItem
            key={metric.id}
            size="medium"
            title={metric.title[lang]}
            chip={{
              color: translateMetricStatus[metric.status].color,
              label: translateEnum('metric_statuses', metric.status).title
            }}
            variant="metric"
          />
        )
      ])
    ]
  }, [metrics, dashboard.data.metrics, isAdmin])

  if (noAccess.length > 0) {
    return (
      <StatusBox
        severity="info"
        title="Åtkomst till ett eller flera mätetal saknas"
        sx={{ mt: 2 }}
      >
        <Text variant="body1">
          För att aktivera en dashboard behöver du ha åtkomst till samtliga
          mätetal som används. Du saknar åtkomst till:
        </Text>
        <List dense={true}>{noAccess}</List>
      </StatusBox>
    )
  }

  if (unsupported.length > 0) {
    return (
      <StatusBox
        severity="info"
        title="Ett eller flera mätetal är ej tillgängliga"
        sx={{ mt: 2 }}
      >
        <Text variant="body1">
          Följande mätetal kommer ej fungera i dashboarden:
        </Text>
        <List dense={true} sx={{ maxWidth: 300 }}>
          {unsupported}
        </List>
      </StatusBox>
    )
  }

  return null
}

export default MetricList
